import React, {
  createContext,
  useContext,
  useEffect,
  useState,
  useCallback,
} from 'react'
import {
  CognitoIdentityProviderClient,
  GetUserCommand,
  GlobalSignOutCommand,
} from '@aws-sdk/client-cognito-identity-provider'
import AuthSession from 'admin/utils/AuthSession'
import { decodedToken } from 'utils/aws/authService'
import { handleLogOut } from 'utils/aws/authService'
import { accessToken } from 'utils/aws/authService'
import { Navigate } from 'react-router-dom'
import { LoadingBars } from 'components/Loading'
import { Box } from '@mui/material'
import { CenteredLoadingContainer } from 'components/Loading'
import brandNavy from 'assets/images/parco.webp'

const AuthContext = createContext(null)

// Create a custom hook to use the AuthContext
export const useAuth = () => {
  return useContext(AuthContext)
}

// Provider component that wraps your app and makes auth state available
export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null)
  const [userAttributes, setUserAttributes] = useState(null)
  const [loading, setLoading] = useState(true)
  const [isFederated, setIsFederated] = useState(false)

  // Sign-out function that dynamically signs the user out
  const signOut = useCallback(
    async (callbackFn) => {
      // console.log('signing out?')
      const token = await accessToken()

      if (!token) return

      // If federated, redirect to provider's sign-out URL
      // this would log the user out of google or facebook - I don't think we want this, but leaving it just in case
      if (isFederated) {
        // For example, if the user signed in with Google
        // window.location.href = 'https://accounts.google.com/logout' // Adjust based on your federated provider
        // return
      }

      // Otherwise, perform global sign-out via Cognito
      const signOutUser = async () => {
        try {
          const client = new CognitoIdentityProviderClient({
            region: 'us-east-2',
          })
          const command = new GlobalSignOutCommand({ AccessToken: token })
          await client.send(command)
        } catch (error) {
          if (
            error.name === 'NotAuthorizedException' &&
            error.message.includes('expired')
          ) {
            console.warn('Access token expired — skipping global sign out')
          } else {
            console.error('Error signing out user:', error)
          }
        }
      }
      // Clear tokens from local storage
      await handleLogOut()
      setUser(null)
      callbackFn && callbackFn()
      await signOutUser()
    },
    [isFederated]
  )

  useEffect(() => {
    const checkAuthStatus = async () => {
      const client = new CognitoIdentityProviderClient({ region: 'us-east-2' })
      const token = await AuthSession.getItem('accessToken') // Store tokens securely in your real app

      const { isFederatedUser } = await decodedToken()

      setIsFederated(isFederatedUser)
      if (!token) {
        setUser(null)
        setLoading(false)
        return
      }

      try {
        const command = new GetUserCommand({
          AccessToken: token,
        })
        const response = await client.send(command)
        const attributes = Object.fromEntries(
          response.UserAttributes.map((attr) => [attr.Name, attr.Value])
        )
        setUser(response) // User is authenticated, set the user
        setUserAttributes(attributes)
      } catch (error) {
        console.error('Authentication failed:', error)
        setUser(null)
      } finally {
        setLoading(false)
      }
    }

    checkAuthStatus()
  }, [])

  const value = {
    user,
    userAttributes,
    loading,
    isAuthenticated: !!user,
    signOut,
  }

  return (
    <AuthContext.Provider value={value}>
      {loading ? (
        // <LoadingBars />
        <CenteredLoadingContainer
          topComponent={
            <Box
              sx={{ paddingTop: '5px' }}
              component="img"
              src={brandNavy}
              alt="PARCO Logo"
            />
          }
        />
      ) : user ? (
        children
      ) : (
        <Navigate to={`/oauth-login?redirect_to=${window.location.href}`} />
      )}
    </AuthContext.Provider>
  )
}
